/**
 * O cone de incerteza da previsão do sistema selecionado (pedido do usuário,
 * 09/10/2026): no lugar da linha do caminho passado e da linha da projeção, a
 * área por onde o CENTRO do sistema deve passar nas próximas horas. Funções
 * puras, em coordenadas do mapa (a projeção geoestacionária, em metros) —
 * testadas em `cone.test.ts`. O desenho foi escolhido entre variantes por três
 * agentes julgadores (previsão, design e uso).
 *
 * O centro de cada antecedência é o da previsão do pyForTraCC (o contorno
 * transladado pelo vetor médio, `registro.ts`), então os centros ficam numa
 * reta. Em volta de cada um, uma ELIPSE de incerteza, alinhada ao caminho:
 * - de lado, a distância percorrida × tan(σ do rumo), σ medido em trechos de
 *   30 min nas últimas 2 h da trajetória (de 10 em 10 min o centro treme com a
 *   borda do contorno e o rumo parece mais incerto do que é);
 * - na frente e atrás, a distância × σ relativa da velocidade;
 * - nos dois, um piso por hora de antecedência (o sistema quase parado vira um
 *   disco: pode ir para qualquer lado).
 * Com pouca história, o σ observado pesa menos e o teto pesa mais
 * (`dispersaoDoTrajeto`). E o cone não passa de `SEMIEIXO_MAX_M` de lado nem
 * de frente: pedido do usuário, "cuidado para o cone não ficar muito grande".
 *
 * O pyForTraCC não publica o erro da previsão, então isto estima o
 * espalhamento pelo movimento do próprio sistema; não é uma probabilidade
 * calibrada (calibrar: comparar, na Oracle, o centro previsto com o observado).
 *
 * O cone é o envoltório convexo das elipses (exato, com os centros numa reta e
 * as elipses alinhadas a ela); as faixas de hora são o mesmo envoltório até
 * +1 h, +2 h e +3 h.
 */
import type { Coordinate } from 'ol/coordinate';

/** Dispersão do rumo (graus): mínima e teto. */
export const SIGMA_RUMO_MIN = 8;
export const SIGMA_RUMO_MAX = 22;
/** Dispersão da velocidade, relativa à média: mínima e teto. */
export const SIGMA_VEL_MIN = 0.1;
export const SIGMA_VEL_MAX = 0.35;
/** Crescimento mínimo dos semieixos: metros por hora de antecedência. */
export const PISO_M_POR_H = 10_000;
/** Semieixo na origem (m): o cone nasce estreito, mas não num ponto. */
export const RAIO_INICIAL_M = 4000;
/** Teto dos semieixos (m), em qualquer antecedência. */
export const SEMIEIXO_MAX_M = 70_000;
/** Pontos da trajetória usados na dispersão: as últimas 2 h (doze imagens de 10 min). */
export const PASSOS_DA_DISPERSAO = 12;
/** Cada trecho da dispersão cobre três imagens (30 min). */
export const IMAGENS_POR_TRECHO = 3;
/** Peso da história: com `n` trechos, o σ observado vale n/(n + PESO_DA_HISTORIA) e o teto, o resto. */
export const PESO_DA_HISTORIA = 4;
/** Sistema com menos que isto de trajetória (pontos, de 10 em 10 min) é novo: o cone vai só até +1 h. */
export const PONTOS_DO_SISTEMA_NOVO = 4;
export const HORIZONTE_DO_NOVO_MIN = 60;
/** Pontos por elipse no envoltório. */
const LADOS = 48;

export interface Dispersao {
  /** σ do rumo (graus). */
  rumo: number;
  /** σ da velocidade, relativa à média. */
  vel: number;
}

/** Diferença entre dois ângulos, em (−π, π]. */
function difAngulo(a: number, b: number): number {
  let d = a - b;
  while (d <= -Math.PI) d += 2 * Math.PI;
  while (d > Math.PI) d -= 2 * Math.PI;
  return d;
}

const limitar = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * A dispersão do movimento nas últimas 2 h da trajetória passada (um ponto por
 * imagem), em trechos de 30 min deslizando de imagem em imagem. Sem trechos,
 * o teto; com poucos, a média ponderada entre o observado e o teto — três
 * trechos sobrepostos subestimariam o σ.
 */
export function dispersaoDoTrajeto(trajeto: readonly Coordinate[] | null): Dispersao {
  const teto = { rumo: SIGMA_RUMO_MAX, vel: SIGMA_VEL_MAX };
  if (!trajeto || trajeto.length <= IMAGENS_POR_TRECHO) return teto;
  const pts = trajeto.slice(-(PASSOS_DA_DISPERSAO + 1));
  const trechos: { a: number; d: number }[] = [];
  for (let i = IMAGENS_POR_TRECHO; i < pts.length; i++) {
    const dx = pts[i][0] - pts[i - IMAGENS_POR_TRECHO][0];
    const dy = pts[i][1] - pts[i - IMAGENS_POR_TRECHO][1];
    const d = Math.hypot(dx, dy);
    if (d > 0) trechos.push({ a: Math.atan2(dy, dx), d });
  }
  if (!trechos.length) return teto;
  // Rumo médio pela soma dos vetores unitários; σ pela distância angular de cada trecho a ele.
  const medio = Math.atan2(
    trechos.reduce((s, p) => s + Math.sin(p.a), 0),
    trechos.reduce((s, p) => s + Math.cos(p.a), 0),
  );
  const sRumo = (Math.sqrt(trechos.reduce((s, p) => s + difAngulo(p.a, medio) ** 2, 0) / trechos.length) * 180) / Math.PI;
  const dMedia = trechos.reduce((s, p) => s + p.d, 0) / trechos.length;
  const sVel = Math.sqrt(trechos.reduce((s, p) => s + (p.d - dMedia) ** 2, 0) / trechos.length) / dMedia;
  const w = trechos.length / (trechos.length + PESO_DA_HISTORIA);
  return {
    rumo: limitar(w * sRumo + (1 - w) * SIGMA_RUMO_MAX, SIGMA_RUMO_MIN, SIGMA_RUMO_MAX),
    vel: limitar(w * sVel + (1 - w) * SIGMA_VEL_MAX, SIGMA_VEL_MIN, SIGMA_VEL_MAX),
  };
}

/** O sistema é novo (pouca trajetória): o cone vai só até `HORIZONTE_DO_NOVO_MIN`. */
export const sistemaNovo = (trajeto: readonly Coordinate[] | null) => !trajeto || trajeto.length < PONTOS_DO_SISTEMA_NOVO;

/** Até que antecedência (min) o cone vai: a última, ou +1 h no sistema novo. */
export function horizonteDoCone(hs: readonly number[], trajeto: readonly Coordinate[] | null): number {
  const ultima = hs.length ? hs[hs.length - 1] : 0;
  return sistemaNovo(trajeto) ? Math.min(ultima, HORIZONTE_DO_NOVO_MIN) : ultima;
}

/** Os semieixos (m) a `h` minutos, tendo o centro andado `distancia` metros: [na frente, de lado]. */
export function semieixos(h: number, distancia: number, d: Dispersao): [number, number] {
  const piso = (PISO_M_POR_H * h) / 60;
  const frente = RAIO_INICIAL_M + Math.max(piso, distancia * d.vel);
  const lado = RAIO_INICIAL_M + Math.max(piso, distancia * Math.tan((d.rumo * Math.PI) / 180));
  return [Math.min(SEMIEIXO_MAX_M, frente), Math.min(SEMIEIXO_MAX_M, lado)];
}

export interface Elipse {
  /** Antecedência (min); 0 = a origem. */
  h: number;
  centro: [number, number];
  /** Semieixo ao longo do caminho e de lado (m). */
  frente: number;
  lado: number;
  /** Rumo do caminho (rad, a partir do eixo x do mapa). */
  angulo: number;
}

/**
 * As elipses: a origem (h = 0) e uma por antecedência. O caminho é a reta da
 * origem ao último centro; parado, o eixo x (a elipse vira círculo pelo piso).
 */
export function elipsesDoCone(origem: [number, number], passos: readonly { h: number; centro: [number, number] }[], d: Dispersao): Elipse[] {
  const fim = passos[passos.length - 1]?.centro ?? origem;
  const angulo = Math.atan2(fim[1] - origem[1], fim[0] - origem[0]);
  const out: Elipse[] = [{ h: 0, centro: origem, frente: RAIO_INICIAL_M, lado: RAIO_INICIAL_M, angulo }];
  for (const p of passos) {
    const [frente, lado] = semieixos(p.h, Math.hypot(p.centro[0] - origem[0], p.centro[1] - origem[1]), d);
    out.push({ h: p.h, centro: p.centro, frente, lado, angulo });
  }
  return out;
}

/** Pontos do contorno de uma elipse. */
export function pontosDaElipse(e: Elipse, lados = LADOS): Coordinate[] {
  const c = Math.cos(e.angulo);
  const s = Math.sin(e.angulo);
  const out: Coordinate[] = [];
  for (let i = 0; i < lados; i++) {
    const t = (2 * Math.PI * i) / lados;
    const x = e.frente * Math.cos(t);
    const y = e.lado * Math.sin(t);
    out.push([e.centro[0] + x * c - y * s, e.centro[1] + x * s + y * c]);
  }
  return out;
}

/** O segmento que atravessa o cone em `e`, de um lado ao outro (a marca do horário): [esquerda, direita] do caminho. */
export function travessa(e: Elipse): [Coordinate, Coordinate] {
  const nx = -Math.sin(e.angulo);
  const ny = Math.cos(e.angulo);
  return [
    [e.centro[0] + nx * e.lado, e.centro[1] + ny * e.lado],
    [e.centro[0] - nx * e.lado, e.centro[1] - ny * e.lado],
  ];
}

/** Envoltório convexo (cadeia monótona), no sentido anti-horário, fechado. */
export function envoltorio(pontos: readonly Coordinate[]): Coordinate[] {
  const p = [...pontos].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cruz = (o: Coordinate, a: Coordinate, b: Coordinate) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const baixo: Coordinate[] = [];
  for (const q of p) {
    while (baixo.length >= 2 && cruz(baixo[baixo.length - 2], baixo[baixo.length - 1], q) <= 0) baixo.pop();
    baixo.push(q);
  }
  const cima: Coordinate[] = [];
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    while (cima.length >= 2 && cruz(cima[cima.length - 2], cima[cima.length - 1], q) <= 0) cima.pop();
    cima.push(q);
  }
  const anel = [...baixo.slice(0, -1), ...cima.slice(0, -1)];
  return [...anel, anel[0]];
}

/** O anel (anti-horário, fechado) do cone que cobre as elipses até a antecedência `ate` (min). */
export function anelDoCone(elipses: readonly Elipse[], ate = Infinity): Coordinate[] {
  return envoltorio(elipses.filter((e) => e.h <= ate).flatMap((e) => pontosDaElipse(e)));
}
